import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Link as MuiLink,
  useTheme
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';

const Register = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      await registerUser(data.email, data.password);
      navigate('/');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || t('registrationError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          backgroundColor: colors.grey[800]
        }}
      >
        <Typography variant="h3" fontWeight="bold" mb={3} textAlign="center">
          {t('signUp')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t('email')}
            type="email"
            margin="normal"
            {...register('email', {
              required: t('emailRequired'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('invalidEmail')
              }
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={loading}
          />

          <TextField
            fullWidth
            label={t('password')}
            type="password"
            margin="normal"
            {...register('password', {
              required: t('passwordRequired') || 'Password is required',
              validate: {
                minLength: (v) => v.length >= 8 || t('passwordMinLength') || 'Password must be at least 8 characters',
                hasUppercase: (v) => /[A-Z]/.test(v) || t('passwordNeedsUppercase') || 'Password must contain at least one uppercase letter',
                hasLowercase: (v) => /[a-z]/.test(v) || t('passwordNeedsLowercase') || 'Password must contain at least one lowercase letter',
                hasNumber: (v) => /\d/.test(v) || t('passwordNeedsNumber') || 'Password must contain at least one number'
              }
            })}
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={loading}
          />

          <TextField
            fullWidth
            label={t('confirmPassword')}
            type="password"
            margin="normal"
            {...register('confirmPassword', {
              required: t('confirmPasswordRequired'),
              validate: (value) =>
                value === password || t('passwordsDoNotMatch')
            })}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            disabled={loading}
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? t('signingUp') : t('signUp')}
          </Button>

          <Box textAlign="center">
            <Typography variant="body2" color={colors.grey[300]}>
              {t('haveAccount')}{' '}
              <MuiLink
                component={Link}
                to="/login"
                sx={{ color: colors.blueAccent[400], textDecoration: 'none' }}
              >
                {t('login')}
              </MuiLink>
            </Typography>
            <Typography variant="body2" color={colors.grey[300]} mt={1}>
              <MuiLink
                component={Link}
                to="/"
                sx={{ color: colors.blueAccent[400], textDecoration: 'none' }}
              >
                {t('continueAsGuest')}
              </MuiLink>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default Register;
